import { useState, useEffect, useRef } from "react";
import { Revision } from "../../Tabs/RevisionsTab";

type DialogType = "lock" | "unlock" | "replace" | null;

type UseLockSelectionParams = {
  mostRecentRevision: Revision | null;
  lockedRevision: Revision | null;
  lockedRevisionId: string | null;
  lockDisabled: boolean;
  lockDisabledReason?: string;
  unlockDisabled: boolean;
  unlockDisabledReason?: string;
  hasErrors: boolean;
  isLocking: boolean;
  isUnlocking: boolean;
  lockError: Error | null;
  unlockError: Error | null;
  onLock: (revisionId: string) => void;
  onUnlock: (revisionId: string) => void;
  onReplaceLock: (newRevisionId: string) => void;
};

export default function useLockSelection({
  mostRecentRevision,
  lockedRevision,
  lockedRevisionId,
  lockDisabled,
  lockDisabledReason,
  unlockDisabled,
  unlockDisabledReason,
  hasErrors,
  isLocking,
  isUnlocking,
  lockError,
  unlockError,
  onLock,
  onUnlock,
  onReplaceLock,
}: UseLockSelectionParams) {
  const [dialog, setDialog] = useState<DialogType>(null);

  const wasLoadingRef = useRef(false);
  const lastOperationRef = useRef<"lock" | "unlock" | null>(null);

  useEffect(() => {
    if (isLocking) {
      wasLoadingRef.current = true;
      lastOperationRef.current = "lock";
    } else if (isUnlocking) {
      wasLoadingRef.current = true;
      lastOperationRef.current = "unlock";
    } else if (wasLoadingRef.current) {
      wasLoadingRef.current = false;
      const relevantError =
        lastOperationRef.current === "lock" ? lockError : unlockError;
      if (!relevantError) {
        setDialog(null);
      }
    }
  }, [isLocking, isUnlocking, lockError, unlockError]);

  const showMostRecent = !!mostRecentRevision;
  const hasLockedRevision = !!lockedRevisionId;
  const isCurrentlyLocked =
    !!lockedRevisionId && lockedRevisionId === lockedRevision?.id;
  const lockedIsMostRecent =
    !!lockedRevisionId &&
    !!mostRecentRevision &&
    lockedRevisionId === mostRecentRevision.id;

  const handleLockClick = () => {
    if (!mostRecentRevision) return;
    if (lockedRevisionId === mostRecentRevision.id) return;
    if (lockedRevisionId && lockedRevisionId !== mostRecentRevision.id) {
      setDialog("replace");
    } else {
      setDialog("lock");
    }
  };
  const handleUnlockClick = () => {
    setDialog("unlock");
  };

  const handleConfirmLock = () => {
    if (mostRecentRevision) onLock(mostRecentRevision.id);
  };

  const handleConfirmUnlock = () => {
    if (lockedRevisionId) onUnlock(lockedRevisionId);
  };

  const handleConfirmReplace = () => {
    if (mostRecentRevision) onReplaceLock(mostRecentRevision.id);
  };

  const handleCancelDialog = () => {
    setDialog(null);
  };

  const lockTooltipText = hasErrors
    ? `Resolve ${mostRecentRevision?.totalErrors ?? 0} fatal error${(mostRecentRevision?.totalErrors ?? 0) === 1 ? "" : "s"} before locking`
    : lockDisabled
      ? lockDisabledReason || "Locking is not available"
      : "Lock this revision for certification";

  const unlockTooltipText = unlockDisabled
    ? unlockDisabledReason || "Unlocking is not available"
    : "Unlock this revision to make changes";

  return {
    dialog,
    showMostRecent,
    hasLockedRevision,
    isCurrentlyLocked,
    lockedIsMostRecent,
    lockTooltipText,
    unlockTooltipText,
    handleLockClick,
    handleUnlockClick,
    handleConfirmLock,
    handleConfirmUnlock,
    handleConfirmReplace,
    handleCancelDialog,
  };
}
